import { useEffect, useMemo, useRef, useState } from "react";
import { accountService, taskService } from "@miplatform/api";
import {
  HTTP_STATUSES,
  ISSUE_IMAGE_TYPES,
  ISSUE_IMAGE_TYPE_TO_LABEL,
  ISSUE_STATUSES,
  ISSUE_TYPES,
  VALID_IMAGE_FILE_MIME_TYPES,
  VIDEO_FILE_MIME_TYPES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { MiTHEME } from "@miplatform/theme";
import { toastService } from "@miplatform/toast";
import {
  createUUID,
  externalReferenceId,
  getDocumentTypeByContentType,
  getFileLabelByType,
  readFileIntoMemory,
} from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import * as mdi from "@mdi/js";
import { useGlobalManagerApproval } from "@mibuildweb/components";

const ISSUE_RESOLUTION_OPTIONS = {
  REPAIRED: {
    icon: mdi.mdiCheckboxMarked,
    text: "Repair Complete, Return Not Required",
    color: MiTHEME.colors.success.main,
    value: "REPAIR",
  },
  NOT_REPAIRED_NO_RETURN: {
    icon: mdi.mdiCloseBox,
    text: "Repair Incomplete, Return Not Required",
    color: MiTHEME.colors.grey.darkerAccent,
    value: "INCOMPLETE_REPAIR_NO_RETURN",
  },
  NOT_REPAIRED_RETURN: {
    icon: mdi.mdiCloseBox,
    text: "Repair Incomplete, Return Required",
    color: MiTHEME.colors.error.main,
    value: "INCOMPLETE_REPAIR_RETURN",
  },
};

const issueStatusResolutionMappings = {
  [ISSUE_RESOLUTION_OPTIONS.REPAIRED.value]: ISSUE_STATUSES.CLOSED,
  [ISSUE_RESOLUTION_OPTIONS.NOT_REPAIRED_NO_RETURN.value]:
    ISSUE_STATUSES.CANCELLED,
  [ISSUE_RESOLUTION_OPTIONS.NOT_REPAIRED_RETURN.value]:
    ISSUE_STATUSES.CONFIRMED,
};

export const useClosingoutViewModel = ({
  orderTemplateId,
  getAttempt = () => {},
  attempt = {},
  issueIndex = 0,
  selectedIssue = null,
}) => {
  const stateManager = useComponentState();
  const { executeApiRequest } = useGlobalManagerApproval();
  const pendingDeletionDocumentsRef = useRef([]);

  const [invoice, setInvoice] = useState(null);

  const { uploadDocument, removeDocument } = useDocumentsViewModel();

  const [issueImages, setIssueImages] = useState([]);
  const [repairImages, setRepairImages] = useState([]);
  const [assessImages, setAssessImages] = useState([]);
  const [reviewImages, setReviewImages] = useState([]);
  const [imagesInViewer, setImagesInViewer] = useState([]);
  const [showImagesViewer, setShowImagesViewer] = useState(false);
  const [isCheckedStep, setIsCheckedStep] = useState(false);
  const [fetchedIssue, setFetchedIssue] = useState();
  const [pendingImageReadCount, setPendingImageReadCount] = useState(0);

  const [issueTemplates, setIssueTemplates] = useState(null);

  const getIssue = ({ id }) =>
    stateManager.run(async () => {
      const res = await taskService.issues.getById({
        id,
        toastMessageConfig: {
          failure: {
            text: "Failed to load issue.",
          },
        },
      });

      if (res.status === HTTP_STATUSES.OK) {
        setFetchedIssue(res.data);

        setIsCheckedStep(
          res.data?.issueResults?.find(
            (issueResult) => issueResult.attemptId === attempt.id
          )?.reviewedByDispatcher || false
        );
      } else {
        setFetchedIssue(undefined);
      }

      return res;
    });

  const { issues = [] } = attempt;
  const attemptIssue = useMemo(
    () => selectedIssue || issues[issueIndex] || {},
    [issues, selectedIssue, issueIndex]
  );
  const issueId = attemptIssue.id;

  const issueIdRef = useRef(issueId);

  useEffect(() => {
    issueIdRef.current = issueId;
  }, [issueId]);

  useEffect(() => {
    if (issueId) {
      getIssue({ id: issueId });
    }
  }, [issueId]);

  const currentIssueResult = useMemo(() => {
    if (fetchedIssue?.id !== issueId) return null;

    return (
      fetchedIssue?.issueResult ||
      fetchedIssue?.issueResults?.find(
        (issueResult) => issueResult.attemptId === attempt.id
      )
    );
  }, [attempt.id, fetchedIssue, issueId]);

  const currentIssue = useMemo(
    () =>
      fetchedIssue?.id === issueId
        ? { ...attemptIssue, ...fetchedIssue, issueResult: currentIssueResult }
        : attemptIssue,
    [attemptIssue, currentIssueResult, fetchedIssue, issueId]
  );

  const COMPLETED_STATUSES = [
    ISSUE_RESOLUTION_OPTIONS.NOT_REPAIRED_NO_RETURN.value,
    ISSUE_RESOLUTION_OPTIONS.REPAIRED.value,
  ];

  const getInvoice = (invoiceId) => {
    stateManager.run(async () => {
      const { status, data = [] } = await accountService.invoices.getById({
        id: invoiceId,
      });

      if (status === HTTP_STATUSES.OK) {
        setInvoice(data);
      }
    });
  };

  const uploadImages = async ({ issue, issueResultExternalReferenceId }) => {
    const imageGroups = [
      { documents: issueImages, imageType: ISSUE_IMAGE_TYPES.ORIGINATING },
      { documents: repairImages, imageType: ISSUE_IMAGE_TYPES.RESOLVING },
      { documents: assessImages, imageType: ISSUE_IMAGE_TYPES.ASSESS },
      { documents: reviewImages, imageType: ISSUE_IMAGE_TYPES.REVIEW },
    ];

    await Promise.all(
      imageGroups.flatMap(({ documents, imageType }) => {
        const existingDocumentCount = documents.filter(
          (document) => document.id
        ).length;

        return documents
          .filter((document) => !document.id)
          .map(({ file }, index) => {
            const fileName = `${issue.title} - ${
              ISSUE_IMAGE_TYPE_TO_LABEL[imageType]
            } ${getFileLabelByType(file.type)} ${
              existingDocumentCount + index + 1
            }`;

            return uploadDocument({
              file,
              fileName,
              documentType: getDocumentTypeByContentType(file.type),
              externalData: JSON.stringify({ imageType }),
              externalReferenceId: externalReferenceId.getIssueRefId({
                issueId: issue.id,
              }),
              additionalExternalReferences: issueResultExternalReferenceId,
            });
          });
      })
    );
  };

  const reviewIssue = ({
    attemptId,
    assignmentId,
    userId,
    issue,
    issueDescription,
    diagnosedIssue,
    repairStatus,
    repairDetails,
    orderId,
    issueTemplateId,
    areaName,
    originalDescription,
    reviewDescription,
    incompleteReason,
    verificationRequest,
    verificationResponse,
    isBillable,
    onReviewApproved,
  }) => {
    return stateManager.run(async () => {
      const issueResultId = issue.issueResult
        ? issue.issueResult.id
        : createUUID();

      const data = {
        id: issueResultId,
        issueId: issue.id,
        attemptId,
        description: issueDescription,
        repairDescription: repairDetails,
        title: issue.title,
        type: issue.issueResult?.type || issue.type || ISSUE_TYPES.RECORDABLE,
        areaName: areaName || issue.areaName,
        status: issueStatusResolutionMappings[repairStatus],
        diagnosedIssue,
        isBillable: Boolean(isBillable),
        assignmentId,
        issueTemplateId,
        originalDescription,
        reviewDescription,
        incompleteReason,
        verificationRequest,
        verificationResponse,
        submittedByUserId: userId,
        reviewedByDispatcher: true,
      };

      const handleSuccessfulUpsert = async ({ data: issueResult, status }) => {
        if (status !== HTTP_STATUSES.OK) {
          return;
        }

        await Promise.all([
          ...pendingDeletionDocumentsRef.current.map((document) =>
            removeDocument(document)
          ),
          uploadImages({
            issue,
            issueResultExternalReferenceId:
              externalReferenceId.getIssueResultRefId({
                orderId,
                attemptId: issueResult.attemptId,
                issueId: issueResult.issueId,
                assignmentId,
                issueResultId: issueResult.id,
              }),
          }),
        ]);

        getAttempt();
        await onReviewApproved?.();
      };

      const options = await taskService.issuesResults.getUpsertOptions({
        data,
      });

      await executeApiRequest({
        options,
        toastMessageForManagerApproval: {
          text: "Manager Approval Required",
          subtext:
            "Changing Diagnosed Task or Is Billable requires manager approval.",
        },
        callback: handleSuccessfulUpsert,
      });
    });
  };

  const getFileValidationError = (file, maxFileSizeInMB = 30) => {
    if (file.size > maxFileSizeInMB * 10 * 1024 * 1024) {
      return `File must be less than ${maxFileSizeInMB}MB.`;
    }

    if (
      ![...VALID_IMAGE_FILE_MIME_TYPES, ...VIDEO_FILE_MIME_TYPES].includes(
        file.type
      )
    ) {
      return "File is the wrong format.";
    }
    return null;
  };

  const toImageEntry = (file) => ({
    file,
    tempURL: URL.createObjectURL(file),
    contentType: file.type,
  });

  const handleAddImages = async (e, setImages) => {
    e.preventDefault();
    const files = Array.from(e.target?.files || []);

    const acceptedFiles = [];
    let rejectionReason = null;

    files.forEach((file) => {
      const error = getFileValidationError(file);

      if (error) {
        rejectionReason = rejectionReason || error;
        return;
      }

      acceptedFiles.push(file);
    });

    if (rejectionReason) {
      toastService.error({
        text: "File could not be added.",
        subtext: rejectionReason,
      });
    }

    if (acceptedFiles.length === 0) {
      return;
    }

    const originatingIssueId = issueId;

    setPendingImageReadCount((count) => count + 1);

    try {
      const readResults = await Promise.allSettled(
        acceptedFiles.map(readFileIntoMemory)
      );

      // The user may have moved to another issue while the files were being read.
      if (issueIdRef.current !== originatingIssueId) {
        return;
      }

      const loadedFiles = readResults
        .filter(({ status }) => status === "fulfilled")
        .map(({ value }) => value);

      if (loadedFiles.length < acceptedFiles.length) {
        toastService.error({
          text: "Some files could not be added.",
          subtext: "They could not be read. Please try again.",
        });
      }

      setImages((prev) => [
        ...loadedFiles.map(toImageEntry).reverse(),
        ...prev,
      ]);
    } finally {
      setPendingImageReadCount((count) => count - 1);
    }
  };

  const handleAddIssueImage = (e) => handleAddImages(e, setIssueImages);

  const handleAddRepairImage = (e) => {
    handleAddImages(e, setRepairImages);
  };

  const handleAddAssessImage = (e) => {
    handleAddImages(e, setAssessImages);
  };

  const handleAddReviewImage = (e) => {
    handleAddImages(e, setReviewImages);
  };

  const removeImage = (document, index, images, setImages) => {
    if (document.id) {
      pendingDeletionDocumentsRef.current.push(document);
    }
    const documentIndex = images.findIndex(
      (image) =>
        image === document ||
        (document.id && image.id === document.id) ||
        (document.tempURL && image.tempURL === document.tempURL)
    );
    const filteredImages = images.filter(
      (_, imageIndex) =>
        imageIndex !== (documentIndex === -1 ? index : documentIndex)
    );
    setImages(filteredImages);
    setImagesInViewer(filteredImages);
    if (filteredImages.length === 0) {
      setShowImagesViewer(false);
    }
  };

  const removeIssueImage = (document, index) =>
    removeImage(document, index, issueImages, setIssueImages);

  const removeRepairImage = (document, index) => {
    removeImage(document, index, repairImages, setRepairImages);
  };

  const removeAssessImage = (document, index) =>
    removeImage(document, index, assessImages, setAssessImages);

  const removeReviewImage = (document, index) =>
    removeImage(document, index, reviewImages, setReviewImages);

  const filterIssueFiles = (documents) => {
    const resolvingDocuments = [];
    const originatedDocuments = [];
    const accessDocuments = [];
    const reviewDocuments = [];

    documents?.forEach((image) => {
      const externalData = JSON.parse(image.externalData);

      if (externalData.imageType === ISSUE_IMAGE_TYPES.ORIGINATING) {
        originatedDocuments.push(image);
      }

      if (externalData.imageType === ISSUE_IMAGE_TYPES.RESOLVING) {
        resolvingDocuments.push(image);
      }

      if (externalData.imageType === ISSUE_IMAGE_TYPES.ASSESS) {
        accessDocuments.push(image);
      }

      if (externalData.imageType === ISSUE_IMAGE_TYPES.REVIEW) {
        reviewDocuments.push(image);
      }
    });

    return {
      resolvingDocuments,
      originatedDocuments,
      accessDocuments,
      reviewDocuments,
    };
  };

  const isStatusRepair = (status) => {
    return status === ISSUE_RESOLUTION_OPTIONS.REPAIRED.value || !status;
  };

  const getTaskResolutionStatus = (issue) => {
    const status = issue.issueResult?.status ?? issue.status;

    let resolutionStatus = null;
    Object.keys(issueStatusResolutionMappings)?.forEach((mapKey) => {
      if (issueStatusResolutionMappings[mapKey] === status) {
        resolutionStatus = mapKey;
      }
    });

    return resolutionStatus;
  };

  const getIssueTemplates = () =>
    stateManager.run(async () => {
      const res = await taskService.orderTemplates.getById({
        id: orderTemplateId,
      });

      if (res.status === HTTP_STATUSES.OK) {
        setIssueTemplates(res.data.issueTemplates);
      }
    });

  return {
    ISSUE_RESOLUTION_OPTIONS,
    issueImages,
    repairImages,
    showImagesViewer,
    imagesInViewer,
    setImagesInViewer,
    setShowImagesViewer,
    setIssueImages,
    setRepairImages,
    assessImages,
    setAssessImages,
    reviewImages,
    setReviewImages,
    handleAddIssueImage,
    handleAddRepairImage,
    handleAddAssessImage,
    handleAddReviewImage,
    removeIssueImage,
    removeRepairImage,
    removeAssessImage,
    removeReviewImage,
    filterIssueFiles,
    isStatusRepair,
    COMPLETED_STATUSES,
    invoice,
    getInvoice,
    reviewIssue,
    getTaskResolutionStatus,
    setInvoice,
    issueTemplates,
    getIssueTemplates,
    currentIssue,
    currentIssueResult,
    loading: stateManager.isBusy(),
    isAddingImages: pendingImageReadCount > 0,
    isCheckedStep,
    setIsCheckedStep,
  };
};
